import { INVOICE_EDITABLE_KEYS } from '@metycle/shared';

import { operatorSuppliedOverrideFields } from '../field-model/invoice-field-model';

import type { InvoiceRenderContext } from '../api/use-invoice-render-context';
import type { ReviewInvoiceInput } from '../api/use-review-invoice-mutation';

export type OverrideScalar = string | number | null;

// The system resolution per review-owned key, normalized to the display
// string the form compares against ('' when the document renders an em-dash).
export type ReviewSystemValues = Readonly<Record<string, string>>;

export interface EditState {
  readonly description: string;
  readonly bankId: string;
  // The operator-supplied BL-body fields, keyed by editable field key
  // (see BL_BODY_OPERATOR_FIELDS). Each holds the value the document renders —
  // prefilled with the override or the system resolution, never blank-means-
  // system.
  readonly operatorFields: Record<string, string>;
}

export const EMPTY_EDIT: EditState = { description: '', bankId: '', operatorFields: {} };

// The material-description key. The authoritative editable partition is the
// server-side allowlist (shared/invoice-editable-fields.ts), which additionally
// permits counterparty_vat_id + vat_note_html — those are not surfaced in the
// dialog yet (finance field-split follow-up) but are carried through on save so
// a review never silently drops them.
const KEY_DESCRIPTION = 'description_of_goods';

// Operator-supplied delivery / origin / contract fields the collapsed BL body
// renders (Provisional / Final Settlement). No upstream source yet — the
// operator fills them here and each shows as an em-dash on the document until
// then. Derived from the shared field model (in document order); the server
// allowlist is the real boundary — this list only decides what the form
// surfaces and its labels.
export const BL_BODY_OPERATOR_FIELDS: readonly { readonly key: string; readonly label: string }[] =
  operatorSuppliedOverrideFields().map(({ key, label }) => ({ key, label }));

// The BL-based invoice templates (Provisional / Final Settlement) are the only
// kinds whose body binds the material description and the operator-supplied
// fields above; other kinds don't render them, so those edits are only offered
// there. Advance / Adjustment editing lands with its own template wiring.
export function blBodyRendersForKind(invoiceType: string | null): boolean {
  return (
    invoiceType === 'Provisional' ||
    invoiceType === 'Final Settlement' ||
    invoiceType === 'Final_Settlement'
  );
}

// The raw persisted override value for a key (undefined when absent), tolerating
// the audited `{assembled, override}` shape and a legacy bare scalar. Preserves
// an explicit null (override-to-blank).
function rawPersistedOverride(fieldOverrides: unknown, key: string): OverrideScalar | undefined {
  if (typeof fieldOverrides !== 'object' || fieldOverrides === null) return undefined;
  const entry = (fieldOverrides as Record<string, unknown>)[key];
  const value =
    typeof entry === 'object' && entry !== null && 'override' in entry ? entry.override : entry;
  if (value === null || typeof value === 'string' || typeof value === 'number') return value;
  return undefined;
}

// A persisted override rendered for a text field (null/absent → empty box).
function readPersistedOverride(fieldOverrides: unknown, key: string): string {
  const value = rawPersistedOverride(fieldOverrides, key);
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

// Every persisted editable override, keyed by field (null preserved). Used to
// carry overrides the dialog does not currently surface (e.g. counterparty_vat_id)
// so a review save does not silently drop them when the endpoint replaces the set.
export function readPersistedOverrides(fieldOverrides: unknown): Record<string, OverrideScalar> {
  const out: Record<string, OverrideScalar> = {};
  for (const key of INVOICE_EDITABLE_KEYS) {
    const value = rawPersistedOverride(fieldOverrides, key);
    if (value !== undefined) out[key] = value;
  }
  return out;
}

// The review-owned keys the form surfaces.
const REVIEW_FORM_KEYS: readonly string[] = [
  KEY_DESCRIPTION,
  ...BL_BODY_OPERATOR_FIELDS.map(({ key }) => key),
];

// Normalize the render-context read into per-key display strings. Editable
// keys are render-context variable paths by construction (asserted in the
// shared allowlist test), so `values[key]` is the system resolution directly.
export function reviewSystemValues(
  context: Pick<InvoiceRenderContext, 'values'> | undefined,
): ReviewSystemValues {
  const out: Record<string, string> = {};
  for (const key of REVIEW_FORM_KEYS) {
    const value = context?.values[key];
    out[key] = value === null || value === undefined ? '' : String(value).trim();
  }
  return out;
}

// The form state on open: every field shows what the document renders today —
// the persisted override where one exists, else the system resolution. The
// bank shows the persisted selection; when none is persisted it stays
// unselected by design (no auto-pick — the operator actively chooses).
export function hydrateReviewEdit(
  invoice: Readonly<{ field_overrides?: unknown; issuer_bank_id?: string | null }>,
  system: ReviewSystemValues,
): EditState {
  const displayed = (key: string): string => {
    const override = readPersistedOverride(invoice.field_overrides, key);
    return override !== '' ? override : (system[key] ?? '');
  };
  const operatorFields: Record<string, string> = {};
  for (const { key } of BL_BODY_OPERATOR_FIELDS) {
    operatorFields[key] = displayed(key);
  }
  return {
    description: displayed(KEY_DESCRIPTION),
    bankId: invoice.issuer_bank_id ?? '',
    operatorFields,
  };
}

function sameOverrides(
  a: Record<string, OverrideScalar>,
  b: Record<string, OverrideScalar>,
): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) if (a[key] !== b[key]) return false;
  return true;
}

// The text overrides the review form owns for a BL-body invoice: the material
// description plus the operator-supplied delivery/origin/contract fields. Each
// is a plain scalar layered pre-body-bind. Keyed by editable field key.
function formTextOverrides(edit: EditState): Record<string, string> {
  const out: Record<string, string> = { [KEY_DESCRIPTION]: edit.description };
  for (const { key } of BL_BODY_OPERATOR_FIELDS) {
    out[key] = edit.operatorFields[key] ?? '';
  }
  return out;
}

// The full override set after applying the form's text edits over the persisted
// values the form does not own. A field writes an override only when it differs
// from the system resolution — a value equal to the system (or blanked back to
// an empty system value) stores nothing, so resetting a field clears its
// persisted override rather than pinning the same text. Returns undefined when
// nothing changed (so the caller sends no field_overrides — no spurious write).
function nextOverrideSet(
  edit: EditState,
  persisted: Record<string, OverrideScalar>,
  system: ReviewSystemValues,
): Record<string, OverrideScalar> | undefined {
  const form = formTextOverrides(edit);
  const next: Record<string, OverrideScalar> = {};
  // Carry persisted keys the form does not own (avoids a dynamic delete).
  for (const [key, value] of Object.entries(persisted)) {
    if (!(key in form)) next[key] = value;
  }
  for (const [key, raw] of Object.entries(form)) {
    const value = raw.trim();
    if (value !== '' && value !== (system[key] ?? '')) next[key] = value;
  }
  return sameOverrides(next, persisted) ? undefined : next;
}

export interface ReviewBodyArgs {
  readonly edit: EditState;
  readonly blBodyEditable: boolean;
  readonly persisted: Record<string, OverrideScalar>;
  readonly system: ReviewSystemValues;
  readonly persistedBankId: string | null;
}

// Build the review write. The endpoint replaces the override set. The bank is
// sent only when the operator picked one that differs from the persisted
// selection — the prefilled current bank never re-writes itself. Returns null
// when there is nothing to save.
export function buildReviewBody(args: Readonly<ReviewBodyArgs>): ReviewInvoiceInput | null {
  const overrides = args.blBodyEditable
    ? nextOverrideSet(args.edit, args.persisted, args.system)
    : undefined;
  const bankChanged = args.edit.bankId !== '' && args.edit.bankId !== (args.persistedBankId ?? '');
  const body: ReviewInvoiceInput = {
    ...(overrides !== undefined ? { field_overrides: overrides } : {}),
    ...(bankChanged ? { issuer_bank_id: args.edit.bankId } : {}),
  };
  return body.field_overrides === undefined && body.issuer_bank_id === undefined ? null : body;
}
